import type { ReactNode } from 'react';

import type {
  ExactAnswerQuestionData,
  NormalizedQuestionData,
  NumericalQuestionData,
} from '@/features/questions/questionNormalizer';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { MathText } from '@/features/content/MathText';
import {
  STUDENT_TOLERANCE_HELP,
  formatTolerancePercent,
} from '@/features/questions/numericalTolerance';

import styles from './QuestionStatsDisplay.module.css';

interface QuestionStatsDisplayProps {
  hideStats?: boolean;
  question: NormalizedQuestionData;
  showCorrectAnswer?: boolean;
  showEarnedPoints?: boolean;
  showFeedback?: boolean;
  showStats?: boolean;
  showStudentAnswer?: boolean;
}

function formatNumber(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

function formatPoints(earnedPoints: number | undefined, possiblePoints: number) {
  if (earnedPoints === undefined) {
    return undefined;
  }

  return `${formatNumber(earnedPoints)} / ${formatNumber(possiblePoints)} points`;
}

function formatDate(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function formatCorrectAnswer(question: NumericalQuestionData | ExactAnswerQuestionData) {
  if (question.kind === 'numerical') {
    if (question.correctAnswer === undefined) {
      return undefined;
    }

    const exponentSuffix =
      question.correctAnswerExtraExponent !== undefined
        ? `e${question.correctAnswerExtraExponent}`
        : '';
    const unitsSuffix = question.units ? ` ${question.units}` : '';
    return `${formatNumber(question.correctAnswer)}${exponentSuffix}${unitsSuffix}`;
  }

  if (!question.correctAnswer) {
    return undefined;
  }

  return question.units
    ? `${question.correctAnswer} ${question.units}`
    : question.correctAnswer;
}

function getInstructorRate(question: NormalizedQuestionData) {
  if (question.scoreEarned === undefined || question.pointsPossible <= 0) {
    return undefined;
  }

  return Math.max(0, Math.min(100, (question.scoreEarned / question.pointsPossible) * 100));
}

export function QuestionStatsDisplay({
  hideStats = false,
  question,
  showCorrectAnswer = false,
  showEarnedPoints = false,
  showFeedback = false,
  showStats = false,
  showStudentAnswer = false,
}: QuestionStatsDisplayProps) {
  const shouldShowCorrectAnswer = showCorrectAnswer && !hideStats;
  const shouldShowStats = showStats && !hideStats;

  if (
    !shouldShowCorrectAnswer &&
    !showFeedback &&
    !shouldShowStats &&
    !showStudentAnswer
  ) {
    return null;
  }

  const studentScore = formatPoints(question.scoreEarned, question.pointsPossible);
  const feedbackMessage = question.isParticipationOnly
    ? question.feedback?.description ?? 'Credit awarded for participating.'
    : question.feedback?.description;

  // Each line the panel holds. A multiple-choice question marks its correct
  // answer on its options, so with nothing else to say the panel used to be
  // drawn empty (release QA run 202610230712).
  const lines: ReactNode[] = [];

  if (shouldShowStats && !showEarnedPoints && studentScore) {
    lines.push(
      <p className={styles.score} key="score">
        {question.isInstructorView ? 'Average score' : 'Score'}: {studentScore}
      </p>,
    );
  }

  if (question.kind === 'multipleChoice') {
    if (showStudentAnswer && !question.isInstructorView && question.selectedIndex === undefined) {
      lines.push(
        <p className={styles.detail} key="no-answer">
          No answer submitted.
        </p>,
      );
    }
    if (showFeedback && feedbackMessage) {
      lines.push(
        <p className={styles.detail} key="feedback">
          <MathText text={feedbackMessage} />
        </p>,
      );
    }
  }

  if (question.kind === 'numerical' || question.kind === 'exactAnswer') {
    if (showStudentAnswer && !question.isInstructorView) {
      lines.push(
        <p className={styles.detail} key="answer">
          {question.inputValue ? (
            <>
              Your answer: <MathText text={question.inputValue} />
            </>
          ) : (
            'No answer submitted.'
          )}
        </p>,
      );
    }
    if (showFeedback && feedbackMessage) {
      lines.push(
        <p className={styles.detail} key="feedback">
          <MathText text={feedbackMessage} />
        </p>,
      );
    }
    const correctAnswer = formatCorrectAnswer(question);
    if (shouldShowCorrectAnswer && correctAnswer) {
      lines.push(
        <p className={styles.detail} key="correct">
          ✓ Correct answer: <MathText text={correctAnswer} />
        </p>,
      );
    }
    // The value above is one draw from this formula — the answer for the
    // representative numbers in the prompt. The formula is what the question
    // actually asserts, and is the thing an instructor checks when a student
    // disputes a mark. Instructor-only: the normalizer declines to carry it
    // for a student at all.
    if (shouldShowCorrectAnswer && question.answerFormula) {
      lines.push(
        <p className={styles.subtle} key="formula">
          Formula: <MathText text={question.answerFormula} />
        </p>,
      );
    }
    if (
      shouldShowCorrectAnswer &&
      question.kind === 'numerical' &&
      question.errorTolerance !== undefined
    ) {
      lines.push(
        <p className={styles.subtle} key="tolerance">
          Accepted tolerance:{' '}
          <Tooltip content={STUDENT_TOLERANCE_HELP}>
            <button className={styles.toleranceInfo} type="button">
              {formatTolerancePercent(question.errorTolerance)}
            </button>
          </Tooltip>
        </p>,
      );
    }
    const instructorRate = getInstructorRate(question);
    if (shouldShowStats && question.isInstructorView && instructorRate !== undefined) {
      lines.push(
        <p className={styles.subtle} key="rate">
          Approximate correct rate: {Math.round(instructorRate)}%
        </p>,
      );
    }
  }

  if (!shouldShowCorrectAnswer && question.solutionsAvailableAtUtcMilli && !hideStats) {
    lines.push(
      <p className={styles.subtle} key="solutions">
        Solutions available on {formatDate(question.solutionsAvailableAtUtcMilli)}.
      </p>,
    );
  }

  if (lines.length === 0) {
    return null;
  }

  return (
    <section aria-label="Question score details" className={styles.panel}>
      {lines}
    </section>
  );
}
